import { useState, useEffect, useCallback } from "react";
import { Calendar, FileText, Inbox, Activity, Menu, X, Plus, ChevronRight } from "lucide-react";

// Detect tablet viewport (600-1200px width, touch device)
export function useIsTablet() {
  const [isTablet, setIsTablet] = useState(false);
  useEffect(() => {
    const check = () => {
      const w = window.innerWidth;
      const hasTouch = navigator.maxTouchPoints > 0;
      setIsTablet(hasTouch && w >= 600 && w <= 1400);
    };
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);
  return isTablet;
}

const TABS = [
  { id: "calendar", label: "Calendar", icon: Calendar },
  { id: "docs",     label: "Docs",     icon: FileText },
  { id: "inbox",    label: "Inbox",    icon: Inbox },
  { id: "activity", label: "Activity", icon: Activity },
];

/**
 * TabletShell wraps the main dashboard on tablet viewports.
 * - Bottom tab bar for primary navigation
 * - Swipe-in side drawer for sidebar (left) and right panel (right)
 * - FAB for quick capture
 * - activeTab / setActiveTab callbacks let parent switch content
 */
export default function TabletShell({
  children,
  activeTab,
  onTabChange,
  sidebarContent,
  rightPanelContent,
  onQuickCapture,
}) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [rightOpen, setRightOpen] = useState(false);

  // Close drawers on tab change
  useEffect(() => {
    setSidebarOpen(false);
    setRightOpen(false);
  }, [activeTab]);

  // Swipe gesture for side drawers
  useEffect(() => {
    let startX = null;
    let startY = null;
    const onTouchStart = (e) => {
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
    };
    const onTouchEnd = (e) => {
      if (startX === null) return;
      const dx = e.changedTouches[0].clientX - startX;
      const dy = e.changedTouches[0].clientY - startY;
      if (Math.abs(dy) > Math.abs(dx) || Math.abs(dx) < 40) return; // vertical swipe or too short
      if (dx > 0 && startX < 32) setSidebarOpen(true);   // swipe right from left edge
      if (dx < 0 && startX > window.innerWidth - 32) setRightOpen(true); // swipe left from right edge
      if (dx < -60 && sidebarOpen) setSidebarOpen(false);
      if (dx > 60 && rightOpen) setRightOpen(false);
      startX = null;
    };
    window.addEventListener("touchstart", onTouchStart, { passive: true });
    window.addEventListener("touchend", onTouchEnd, { passive: true });
    return () => {
      window.removeEventListener("touchstart", onTouchStart);
      window.removeEventListener("touchend", onTouchEnd);
    };
  }, [sidebarOpen, rightOpen]);

  return (
    <div className="tablet-shell relative flex flex-col h-screen bg-[#080808] overflow-hidden">

      {/* Sidebar drawer — slides from left */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-[200] flex">
          <div className="w-72 h-full bg-[#0D0D0D] border-r border-[#1E1E1E] overflow-y-auto flex-shrink-0 shadow-2xl">
            <div className="flex items-center justify-between px-4 py-3 border-b border-[#1E1E1E]">
              <span className="text-[12px] font-semibold text-[#EDEDED]">Menu</span>
              <button onClick={() => setSidebarOpen(false)} className="p-1.5 rounded-lg text-[#52525B] hover:text-[#EDEDED] hover:bg-[#141414]">
                <X className="w-4 h-4"/>
              </button>
            </div>
            <div className="overflow-y-auto flex-1">{sidebarContent}</div>
          </div>
          <div className="flex-1" onClick={() => setSidebarOpen(false)}/>
        </div>
      )}

      {/* Right panel drawer — slides from right */}
      {rightOpen && (
        <div className="fixed inset-0 z-[200] flex justify-end">
          <div className="flex-1" onClick={() => setRightOpen(false)}/>
          <div className="w-80 h-full bg-[#0D0D0D] border-l border-[#1E1E1E] overflow-y-auto shadow-2xl">
            <div className="flex items-center justify-between px-4 py-3 border-b border-[#1E1E1E]">
              <span className="text-[12px] font-semibold text-[#EDEDED]">Details</span>
              <button onClick={() => setRightOpen(false)} className="p-1.5 rounded-lg text-[#52525B] hover:text-[#EDEDED] hover:bg-[#141414]">
                <X className="w-4 h-4"/>
              </button>
            </div>
            <div className="overflow-y-auto">{rightPanelContent}</div>
          </div>
        </div>
      )}

      {/* Top mini-bar on tablet — menu button + right panel toggle */}
      <div className="flex-shrink-0 flex items-center justify-between px-4 py-2 border-b border-[#141414] bg-[#080808]" style={{ minHeight: 44 }}>
        <button onClick={() => setSidebarOpen(true)} className="p-2.5 rounded-xl text-[#52525B] hover:text-[#EDEDED] hover:bg-[#141414] active:bg-[#1a1a1a] transition-colors">
          <Menu className="w-5 h-5"/>
        </button>
        <span className="text-[13px] font-semibold text-[#EDEDED]" style={{ fontFamily: "'Outfit', sans-serif" }}>
          {TABS.find(t => t.id === activeTab)?.label || "Calendar"}
        </span>
        <button onClick={() => setRightOpen(true)} className="p-2.5 rounded-xl text-[#52525B] hover:text-[#EDEDED] hover:bg-[#141414] active:bg-[#1a1a1a] transition-colors">
          <ChevronRight className="w-5 h-5"/>
        </button>
      </div>

      {/* Main content area */}
      <div className="flex-1 min-h-0 overflow-hidden relative">
        {children}
      </div>

      {/* FAB — quick capture */}
      <button
        onClick={onQuickCapture}
        className="fixed z-[150] flex items-center justify-center rounded-2xl shadow-2xl active:scale-95 transition-transform"
        style={{
          bottom: 72, right: 20, width: 52, height: 52,
          background: "var(--cresc-accent)",
        }}
        aria-label="Quick capture"
      >
        <Plus className="w-6 h-6 text-white"/>
      </button>

      {/* Bottom tab bar */}
      <div className="flex-shrink-0 flex items-stretch border-t border-[#141414] bg-[#080808]" style={{ height: 60, paddingBottom: "env(safe-area-inset-bottom)" }}>
        {TABS.map(tab => {
          const Icon = tab.icon;
          const active = activeTab === tab.id;
          return (
            <button key={tab.id}
              onClick={() => onTabChange(tab.id)}
              className="flex-1 flex flex-col items-center justify-center gap-1 transition-colors active:bg-[#0D0D0D]"
              style={{ color: active ? "#5A90F5" : "#3A3A3A" }}
            >
              <Icon className="w-5 h-5"/>
              <span className="text-[9px] font-medium tracking-wide">{tab.label}</span>
              {active && <div className="absolute top-0 w-8 h-0.5 rounded-full bg-[#5A90F5]"/>}
            </button>
          );
        })}
      </div>
    </div>
  );
}
